/**
 * VehicleInventoryPage — Fahrzeuginventur (Jun-2026, User-Wunsch).
 * Route: /dealer/fahrzeuginventur
 *
 * Kombiniert Bestandsübersicht (mit „Zuletzt gezählt"-Status) und den
 * Scan-/Zähl-Workflow (EAN/QR/FIN-Scan, Pflicht FIN-Foto + Standort,
 * Anomalie-Erkennung).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ClipboardCheck, FileText, Loader2, Smartphone, FileSpreadsheet, Archive } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import VehicleInventoryScanPanel from "../components/dealer/VehicleInventoryScanPanel";
import VehicleInventoryOverview from "../components/dealer/VehicleInventoryOverview";
import InventoryWalkPanel from "../components/dealer/InventoryWalkPanel";
import MobileInventoryQrModal from "../components/dealer/MobileInventoryQrModal";
import DispoCsvImportModal from "../components/dealer/DispoCsvImportModal";
import InventoryWalkArchiveModal from "../components/dealer/InventoryWalkArchiveModal";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function VehicleInventoryPage() {
    const [feed, setFeed] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [prefillCode, setPrefillCode] = useState("");
    const [protoFrom, setProtoFrom] = useState(todayISO());
    const [protoTo, setProtoTo] = useState(todayISO());
    const [downloading, setDownloading] = useState(false);
    const [showWalk, setShowWalk] = useState(false);
    const [showPapersWalk, setShowPapersWalk] = useState(false);
    const [searchParams] = useSearchParams();
    const [showQr, setShowQr] = useState(searchParams.get("qr") === "1");
    // Jun-2026 (User-Wunsch) — aus der Disposition hierher verschoben.
    const [showCsv, setShowCsv] = useState(false);
    const [showWalkArchive, setShowWalkArchive] = useState(false);

    const handleCounted = (count) => {
        setFeed((f) => [count, ...f].slice(0, 20));
        setRefreshKey((k) => k + 1);
    };
    const handlePick = (v) => {
        // Code in den Scan-Bereich übernehmen (FIN bevorzugt, sonst interne Nr.)
        setPrefillCode(v.vin || v.internal_number || "");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const downloadProtocol = async () => {
        setDownloading(true);
        try {
            const r = await api.get("/dealer/vehicle-inventory/protocol.pdf", {
                params: { date_from: protoFrom, date_to: protoTo },
                responseType: "blob",
            });
            previewPdfResponse(r, `Fahrzeuginventur_${protoFrom}_${protoTo}.pdf`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Protokoll konnte nicht erstellt werden");
        } finally { setDownloading(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-6 max-w-6xl">
                <div className="mb-4">
                    <Link to="/dashboard" className="text-xs uppercase tracking-wider font-bold text-gray-500 hover:text-[#111827]">← Dashboard</Link>
                </div>
                <h1 className="font-display text-3xl font-black mb-1 flex items-center gap-2 justify-between flex-wrap" data-testid="vehicle-inventory-page-title">
                    <span className="flex items-center gap-2"><ClipboardCheck className="w-7 h-7 text-[#FFCC00]" /> Fahrzeuginventur</span>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button type="button" onClick={() => setShowCsv(true)} variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white" data-testid="open-csv-import-btn">
                            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> CSV-Import
                        </Button>
                        <Button type="button" onClick={() => setShowWalkArchive(true)} variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white" data-testid="open-walk-archive-btn">
                            <Archive className="w-4 h-4 mr-1.5" /> Inventur-Verlauf
                        </Button>
                        <Button type="button" onClick={() => setShowQr(true)} variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white" data-testid="open-mobile-inventory-qr-btn">
                            <Smartphone className="w-4 h-4 mr-1.5" /> Handy-Inventur (QR)
                        </Button>
                    </div>
                </h1>
                <p className="text-sm text-gray-600 mb-4">
                    Bestand zählen per FIN-/QR-Scan — mit Pflicht-FIN-Foto, Standort und automatischer Anomalie-Erkennung.
                </p>

                {showQr && <MobileInventoryQrModal onClose={() => setShowQr(false)} />}
                {showCsv && (
                    <DispoCsvImportModal
                        onClose={() => setShowCsv(false)}
                        onDone={() => { setShowCsv(false); setRefreshKey((k) => k + 1); }}
                    />
                )}
                {showWalkArchive && (
                    <InventoryWalkArchiveModal onClose={() => setShowWalkArchive(false)} />
                )}

                {/* Inventur-Protokoll (revisionssicher) */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-6 flex flex-wrap items-end gap-3" data-testid="vehicle-inventory-protocol-block">
                    <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#FFCC00]" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Inventur-Protokoll (PDF)</span>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-gray-500">Von</Label>
                        <Input type="date" value={protoFrom} onChange={(e) => setProtoFrom(e.target.value)} className="h-9 rounded-sm font-mono-num w-40" data-testid="vehicle-inventory-protocol-from" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-gray-500">Bis</Label>
                        <Input type="date" value={protoTo} onChange={(e) => setProtoTo(e.target.value)} className="h-9 rounded-sm font-mono-num w-40" data-testid="vehicle-inventory-protocol-to" />
                    </div>
                    <Button type="button" onClick={downloadProtocol} disabled={downloading} className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="vehicle-inventory-protocol-btn">
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" /> Protokoll erstellen</>}
                    </Button>
                </div>

                {/* Inventur-Rundgänge (Fahrzeug + Schlüssel · Papiere) */}
                <div className="mb-6 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {!showWalk && (
                            <Button type="button" onClick={() => setShowWalk(true)} className="h-9 rounded-sm bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-[#111827] font-bold" data-testid="open-inventory-walk-btn">
                                <ClipboardCheck className="w-4 h-4 mr-1.5" /> Inventur-Rundgang starten (Fahrzeug + Schlüssel)
                            </Button>
                        )}
                        {!showPapersWalk && (
                            <Button type="button" onClick={() => setShowPapersWalk(true)} variant="outline" className="h-9 rounded-sm text-sm font-bold bg-white" data-testid="open-papers-walk-btn">
                                <FileText className="w-4 h-4 mr-1.5" /> Inventur Papiere
                            </Button>
                        )}
                    </div>
                    {showWalk && <InventoryWalkPanel mode="full" onClose={() => setShowWalk(false)} />}
                    {showPapersWalk && <InventoryWalkPanel mode="papers" onClose={() => setShowPapersWalk(false)} />}
                </div>

                {/* Scan/Zähl-Workflow */}
                <VehicleInventoryScanPanel
                    key={prefillCode}
                    feed={feed}
                    prefillCode={prefillCode}
                    onCounted={handleCounted}
                />

                {/* Bestandsübersicht */}
                <div className="mt-8">
                    <h2 className="text-lg font-bold mb-3">Bestandsübersicht</h2>
                    <VehicleInventoryOverview refreshKey={refreshKey} onPick={handlePick} />
                </div>
            </div>
        </div>
    );
}
